import { useState } from 'react';
import { useSubscriptionContext } from '../../../contexts/SubscriptionContext';
import { MODULE_CONFIG } from '../../../config/subscriptionConfig';
import { ModuleKey } from '../../../types/subscriptionTypes';
import { formatDate, formatCurrency, calculateProration } from '../../../utils/subscriptionUtils';
import { Btn } from '../../ui';

interface AddOnModuleCardProps {
    moduleKey: ModuleKey;
}

export const AddOnModuleCard = ({ moduleKey }: AddOnModuleCardProps) => {
    const { isModuleActive, isModulePendingCancel, getModuleSubscription, accountStatus } = useSubscriptionContext();
    const [showManageDropdown, setShowManageDropdown] = useState(false);

    const config = MODULE_CONFIG[moduleKey];
    const subscription = getModuleSubscription(moduleKey);
    const active = isModuleActive(moduleKey);
    const pendingCancel = isModulePendingCancel(moduleKey);
    const isLocked = !active;

    // Proration estimate for locked cards (assumes billing anchor day 15 for preview)
    const prorationEstimate = isLocked ? calculateProration(config.monthlyRate, 15) : null;

    return (
        <div
            className={`addon-card ${active ? 'addon-card--active' : 'addon-card--locked'} ${pendingCancel ? 'addon-card--pending-cancel' : ''}`}
        >
            {/* Header */}
            <div className="addon-card-header">
                <div className={`addon-card-icon addon-card-icon--${moduleKey}`}>{getModuleIcon(moduleKey)}</div>
                <div className="addon-card-status">
                    {active && !pendingCancel && (
                        <span className="addon-card-badge addon-card-badge--active">✓ ACTIVE</span>
                    )}
                    {pendingCancel && (
                        <span className="addon-card-badge addon-card-badge--pending">PENDING CANCEL</span>
                    )}
                    {isLocked && <span className="addon-card-badge addon-card-badge--locked">LOCKED</span>}
                </div>
            </div>

            {/* Content */}
            <h4 className="addon-card-name">{config.name}</h4>
            <p className="addon-card-desc">{config.description}</p>

            {/* Features */}
            <ul className="addon-card-features">
                {config.features.map((feature) => (
                    <li key={feature}>
                        <span className="addon-card-feature-check">✓</span>
                        {feature}
                    </li>
                ))}
            </ul>

            {/* Pricing & Date */}
            <div className="addon-card-footer">
                <div className="addon-card-pricing">
                    <span className="addon-card-price">{config.rateLabel}</span>
                    {isLocked && prorationEstimate && (
                        <span className="addon-card-proration">
                            Prorated: {formatCurrency(prorationEstimate.proratedAmount)} today
                        </span>
                    )}
                    {active && subscription && (
                        <span className="addon-card-active-since">
                            Active since {formatDate(subscription.activatedAt)}
                        </span>
                    )}
                </div>

                {/* Actions */}
                <div className="addon-card-actions">
                    {isLocked && accountStatus !== 'trial' && (
                        <Btn
                            variant="primary"
                            buttonSize="sm"
                            buttonText="+ Add to Plan"
                            buttonOnClick={() => {
                                /* TODO: open AddOnCheckoutModal */
                            }}
                        />
                    )}
                    {active && !pendingCancel && (
                        <div className="addon-card-manage-wrapper">
                            <Btn
                                variant="secondary-outlined"
                                buttonSize="sm"
                                buttonText="⇄ Manage"
                                buttonOnClick={() => setShowManageDropdown(!showManageDropdown)}
                            />
                            {showManageDropdown && (
                                <div className="addon-card-dropdown">
                                    <button className="addon-card-dropdown-item">View invoices</button>
                                    <button className="addon-card-dropdown-item addon-card-dropdown-item--danger">
                                        Remove from plan
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    {pendingCancel && subscription?.effectiveEndDate && (
                        <div className="addon-card-pending-info">
                            <span className="addon-card-pending-text">
                                Access until {formatDate(subscription.effectiveEndDate)}
                            </span>
                            <Btn
                                variant="secondary-outlined"
                                buttonSize="sm"
                                buttonText="Reactivate"
                                buttonOnClick={() => {
                                    /* TODO: reactivate module */
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Simple icon placeholder — replace with actual SVG icons later
function getModuleIcon(moduleKey: ModuleKey): string {
    const icons: Record<ModuleKey, string> = {
        pm: '🔧',
        tires: '⊙',
        training: '📋',
        costTracking: '💲',
        skillTracking: '🎯',
    };
    return icons[moduleKey] || '📦';
}
